/**
 * Relatório de Turno — Redesenho v2 (Fev/2026)
 * ================================================
 * Mobile-first, single-screen. Todos os utilizadores vêem tudo.
 * Permissões controlam apenas edição/eliminação/reabertura.
 *
 * Regras:
 *  - 3 filtros no topo: Data, Secção (Todas), Turno (Ambos)
 *  - Por defeito: hoje + Todas as secções + Ambos os turnos
 *  - Cards (sem tabelas)
 *  - Colaborador: cria só nas suas (secção, turno) do dia; pode "Dar como
 *    concluída" qualquer tarefa; pode "Confirmo que li" global por turno
 *  - Admin/Gerente: cria/edita/apaga em qualquer lado; pode "Reabrir"
 *  - "Concluída por X às HH:MM"
 *  - "Confirmo que li" único por (dia, turno)
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import {
  ClipboardList, CheckCircle2, XCircle, AlertCircle, Package,
  MessageSquareText, Plus, Trash2, Edit3, Send, RefreshCw,
  UserCircle2, Info as InfoIcon, ListChecks, Clock, ChevronRight,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TURNOS = [
  { key: 'manha', label: 'Manhã' },
  { key: 'tarde', label: 'Tarde' },
  { key: 'dia', label: 'Dia inteiro' },
];

const AREAS = [
  { key: 'feito', label: 'O que foi feito', icon: CheckCircle2, cor: 'emerald' },
  { key: 'por_fazer', label: 'O que ficou por fazer', icon: ListChecks, cor: 'amber' },
  { key: 'produtos_falta', label: 'Produtos em falta', icon: Package, cor: 'red' },
  { key: 'ocorrencias', label: 'Ocorrências / info p/ turno seguinte', icon: MessageSquareText, cor: 'blue' },
];

const CORES_AREA = {
  emerald: { border: 'border-emerald-200', bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'text-emerald-600' },
  amber:   { border: 'border-amber-200',   bg: 'bg-amber-50',   text: 'text-amber-700',   icon: 'text-amber-600' },
  red:     { border: 'border-red-200',     bg: 'bg-red-50',     text: 'text-red-700',     icon: 'text-red-600' },
  blue:    { border: 'border-blue-200',    bg: 'bg-blue-50',    text: 'text-blue-700',    icon: 'text-blue-600' },
};

const hoje = () => new Date().toISOString().slice(0, 10);
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});
const fmtHora = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-PT', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
};
const fmtHoraCurta = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('pt-PT', {
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
};

// ==================== COMPONENTE PRINCIPAL ====================
const RelatorioTurnoTab = ({ user }) => {
  const [data, setData] = useState(hoje());
  const [filtroSecao, setFiltroSecao] = useState('__todas__');
  const [filtroTurno, setFiltroTurno] = useState('__ambos__');

  const [seccoes, setSeccoes] = useState([]);
  const [relatorios, setRelatorios] = useState([]);
  const [indicacoesDia, setIndicacoesDia] = useState([]);
  const [minhasAtribuicoes, setMinhasAtribuicoes] = useState([]); // [{local, tipo}] p/ o dia
  const [loading, setLoading] = useState(false);
  const [showHistorico, setShowHistorico] = useState(false);

  // Role check defensivo: aceita variantes de admin/gerência
  const roleRaw = (user?.role || '').toString().toLowerCase().trim();
  const isAdmin = ['admin', 'administrador', 'responsavel', 'responsável', 'gerente', 'manager'].includes(roleRaw);
  const meuId = user?.uuid || user?.id || user?.sub;
  const meuNome = user?.nome_completo || user?.nome || user?.username;

  const ehMeu = useCallback((colabId) => {
    if (!colabId) return false;
    const ids = new Set(
      [user?.uuid, user?.id, user?.sub, user?._id]
        .filter(Boolean)
        .map(String)
    );
    return ids.has(String(colabId));
  }, [user]);

  // ---------- Carregamentos ----------
  const carregarSeccoes = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/relatorio-turno/seccoes`, { headers: authHeaders() });
      setSeccoes(r.data || []);
    } catch { toast.error('Falha ao carregar secções'); }
  }, []);

  const carregarDia = useCallback(async () => {
    setLoading(true);
    try {
      const [rels, inds] = await Promise.all([
        axios.get(`${API}/api/relatorio-turno/consultar`, { params: { data }, headers: authHeaders() }),
        axios.get(`${API}/api/relatorio-turno/indicacoes-dia`, { params: { data }, headers: authHeaders() }),
      ]);
      setRelatorios(rels.data || []);
      setIndicacoesDia(inds.data || []);
    } catch { toast.error('Falha ao carregar o dia'); }
    finally { setLoading(false); }
  }, [data]);

  const carregarMinhaEscala = useCallback(async () => {
    if (isAdmin) { setMinhasAtribuicoes([]); return; }
    // Tentativa dupla: horários novos usam UUID, legacy usam ObjectId hex.
    const ids = [user?.uuid, user?.id, user?.sub, user?._id].filter(Boolean);
    if (ids.length === 0) return;
    try {
      const [ano, mes] = data.split('-').map(Number);
      let turnosDia = [];
      for (const cid of ids) {
        try {
          const r = await axios.get(
            `${API}/api/horarios-mensais-admin/horario-mes-colaborador/${cid}`,
            { params: { ano, mes }, headers: authHeaders() }
          );
          const t = r.data?.por_dia?.[data] || [];
          if (t.length > 0) { turnosDia = t; break; }
        } catch { /* tenta o próximo id */ }
      }
      const IGNORAR = new Set(['folga', 'folga_compensacao', 'ferias', 'férias', 'falta', 'baixa']);
      const derivarTurno = (t) => {
        // Convenção do backend (services/vendas_turno_xd.py):
        //   entrada<13 → 'manha'; senão 'tarde'; entrada<13 && saida>15 → também 'dia'
        const parseH = (s) => {
          if (!s || typeof s !== 'string') return null;
          const [h] = s.split(':').map(Number);
          return Number.isFinite(h) ? h : null;
        };
        const he = parseH(t.entrada);
        const hs = parseH(t.saida);
        const turnos = [];
        if (he == null) return ['dia'];
        if (he < 13) turnos.push('manha');
        else turnos.push('tarde');
        if (he < 13 && hs != null && hs > 15) turnos.push('dia');
        return turnos;
      };
      const validos = [];
      for (const t of turnosDia) {
        if (!t.local) continue;
        if (IGNORAR.has((t.tipo || '').toLowerCase())) continue;
        for (const tur of derivarTurno(t)) {
          validos.push({ local: t.local, turno: tur });
        }
      }
      setMinhasAtribuicoes(validos);
    } catch { /* sem escala é aceitável */ }
  }, [data, user, isAdmin]);

  useEffect(() => { carregarSeccoes(); }, [carregarSeccoes]);
  useEffect(() => { carregarDia(); }, [carregarDia]);
  useEffect(() => { carregarMinhaEscala(); }, [carregarMinhaEscala]);

  // Auto-refresh a cada 60s
  useEffect(() => {
    const t = setInterval(carregarDia, 60000);
    return () => clearInterval(t);
  }, [carregarDia]);

  // ---------- Grelha (secção × turno) ----------
  const grelha = useMemo(() => {
    const secList = filtroSecao === '__todas__'
      ? seccoes.map((s) => s.label)
      : [filtroSecao];
    const turList = filtroTurno === '__ambos__'
      ? ['manha', 'tarde']
      : [filtroTurno];
    // Se em modo Ambos há relatórios em turno 'dia', incluir também
    if (filtroTurno === '__ambos__' && relatorios.some((r) => r.turno === 'dia')) {
      turList.push('dia');
    }
    const combos = [];
    for (const sec of secList) {
      for (const tur of turList) {
        const rel = relatorios.find((r) => r.secao === sec && r.turno === tur);
        const inds = indicacoesDia.filter((i) =>
          i.secao === sec &&
          (i.turno_ou_periodo === tur || i.turno_ou_periodo === 'dia' || i.turno_ou_periodo === 'intervalo')
        );
        // Em modo 'Ambos' + 'dia', só mostrar se houver conteúdo
        if (filtroTurno === '__ambos__' && tur === 'dia' && !rel && inds.length === 0) continue;
        combos.push({ secao: sec, turno: tur, relatorio: rel, indicacoes: inds });
      }
    }
    return combos;
  }, [seccoes, filtroSecao, filtroTurno, relatorios, indicacoesDia]);

  // Confirmação global do turno actual (só faz sentido quando um turno é filtrado)
  const turnoAtivo = filtroTurno === '__ambos__' ? null : filtroTurno;
  const jaConfirmeiTurno = useMemo(() => {
    if (!turnoAtivo) return false;
    const relsDoTurno = relatorios.filter((r) => r.turno === turnoAtivo);
    if (relsDoTurno.length === 0) return false;
    return relsDoTurno.every((r) =>
      (r.confirmacoes || []).some((c) => ehMeu(c.colaborador_id))
    );
  }, [relatorios, turnoAtivo, ehMeu]);

  // Normaliza texto (remove acentos + lowercase) para comparações resistentes a "Café" vs "Cafe"
  const normalizar = useCallback((s) =>
    (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim(),
  []);

  // Permissão: pode criar/editar/apagar nesta (secção, turno)?
  const podeAdicionarEm = useCallback((sec, tur) => {
    if (isAdmin) return true;
    // Se não conseguimos carregar a escala do colaborador (endpoint offline, etc.),
    // não bloqueamos — deixamos o backend decidir com base no auth. Isto evita
    // "nenhum perfil consegue adicionar" quando o endpoint de horário falha.
    if (!minhasAtribuicoes || minhasAtribuicoes.length === 0) return true;
    const secN = normalizar(sec);
    return minhasAtribuicoes.some((a) =>
      normalizar(a.local) === secN && (a.turno === tur || a.turno === 'dia')
    );
  }, [isAdmin, minhasAtribuicoes, normalizar]);

  // ---------- Confirmar dia+turno global ----------
  const confirmarTurnoGlobal = async () => {
    if (!turnoAtivo) return;
    try {
      const r = await axios.post(
        `${API}/api/relatorio-turno/confirmar-dia`,
        {},
        { params: { data, turno: turnoAtivo }, headers: authHeaders() }
      );
      if (r.data?.confirmados > 0) {
        toast.success(`Passagem confirmada em ${r.data.confirmados} secção(ões).`);
      } else if ((r.data?.total_docs || 0) > 0) {
        toast.info('Já tinha confirmado este turno.');
      } else {
        toast.info('Nada a confirmar neste turno.');
      }
      carregarDia();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao confirmar');
    }
  };

  // ==================== RENDER ====================
  return (
    <div className="max-w-3xl mx-auto space-y-3 pb-36" data-testid="relatorio-turno-tab">
      {/* Header + filtros */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white p-4 rounded-2xl shadow-lg">
        <div className="flex items-center gap-2 mb-3">
          <ClipboardList className="w-6 h-6" />
          <h1 className="text-lg font-bold">Relatório de Turno</h1>
          <Badge className="ml-auto bg-white/10 text-white text-[10px] border-white/20">
            {isAdmin ? 'Gerência' : 'Colaborador'}
          </Badge>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-[10px] uppercase text-white/70">Data</Label>
            <input
              type="date"
              data-testid="rt-data-input"
              value={data}
              onChange={(e) => setData(e.target.value)}
              className="w-full bg-white/10 text-white text-sm rounded px-2 py-1.5 border border-white/20"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-white/70">Secção</Label>
            <select
              data-testid="rt-secao-filtro"
              value={filtroSecao}
              onChange={(e) => setFiltroSecao(e.target.value)}
              className="w-full bg-white/10 text-white text-sm rounded px-2 py-1.5 border border-white/20"
            >
              <option value="__todas__" className="text-slate-900">Todas</option>
              {seccoes.map((s) => (
                <option key={s.key} value={s.label} className="text-slate-900">{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-white/70">Turno</Label>
            <select
              data-testid="rt-turno-filtro"
              value={filtroTurno}
              onChange={(e) => setFiltroTurno(e.target.value)}
              className="w-full bg-white/10 text-white text-sm rounded px-2 py-1.5 border border-white/20"
            >
              <option value="__ambos__" className="text-slate-900">Ambos</option>
              {TURNOS.map((t) => (
                <option key={t.key} value={t.key} className="text-slate-900">{t.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2 text-xs text-white/70">
          <UserCircle2 className="w-4 h-4" />
          <span className="truncate">{meuNome}</span>
          <Button
            size="sm"
            variant="outline"
            className="ml-auto h-7 text-xs bg-white/10 text-white border-white/20 hover:bg-white/20"
            onClick={carregarDia}
            data-testid="rt-refresh-btn"
          >
            <RefreshCw className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {loading && <div className="text-center py-4 text-slate-500 text-sm">A carregar…</div>}

      {/* Grelha de blocos (secção × turno) */}
      {grelha.map((combo) => (
        <BlocoRelatorio
          key={`${combo.secao}-${combo.turno}`}
          combo={combo}
          data={data}
          user={user}
          isAdmin={isAdmin}
          podeAdicionar={podeAdicionarEm(combo.secao, combo.turno)}
          ehMeu={ehMeu}
          onChange={carregarDia}
        />
      ))}

      {grelha.length === 0 && !loading && (
        <p className="text-sm text-slate-500 italic text-center py-6">
          Sem secções configuradas.
        </p>
      )}

      {/* Admin: criar indicação */}
      {isAdmin && (
        <BlocoCriarIndicacao
          data={data}
          seccoes={seccoes}
          onCriado={carregarDia}
        />
      )}

      {/* Barra inferior fixa */}
      <div className="fixed bottom-4 left-0 right-0 px-4 z-20">
        <div className="max-w-3xl mx-auto flex gap-2">
          <Button
            variant="outline"
            className="h-11 px-3 bg-white shadow"
            onClick={() => setShowHistorico(true)}
            data-testid="rt-historico-btn"
            title="Histórico"
          >
            <Clock className="w-4 h-4" />
          </Button>
          {turnoAtivo ? (
            <Button
              className={`flex-1 h-11 shadow ${jaConfirmeiTurno ? 'bg-emerald-700 hover:bg-emerald-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
              onClick={confirmarTurnoGlobal}
              disabled={jaConfirmeiTurno}
              data-testid="rt-confirmar-li-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {jaConfirmeiTurno
                ? `✓ Confirmado — ${TURNOS.find((t) => t.key === turnoAtivo)?.label}`
                : `Confirmo que li — ${TURNOS.find((t) => t.key === turnoAtivo)?.label}`}
            </Button>
          ) : (
            <div className="flex-1 h-11 bg-slate-100 rounded-md flex items-center justify-center text-xs text-slate-500 shadow">
              Escolhe um turno para confirmar leitura
            </div>
          )}
        </div>
      </div>

      {showHistorico && (
        <HistoricoModal seccoes={seccoes} onClose={() => setShowHistorico(false)} />
      )}
    </div>
  );
};

// ==================== BLOCO RELATÓRIO (secção × turno) ====================
const BlocoRelatorio = ({ combo, data, user, isAdmin, podeAdicionar, ehMeu, onChange }) => {
  const { secao, turno, relatorio, indicacoes } = combo;
  const [novoTexto, setNovoTexto] = useState({ feito: '', por_fazer: '', produtos_falta: '', ocorrencias: '' });
  const [editando, setEditando] = useState(null);
  const [aberto, setAberto] = useState(true);

  const registosPorArea = useMemo(() => {
    const r = { feito: [], por_fazer: [], produtos_falta: [], ocorrencias: [] };
    (relatorio?.registos || []).forEach((x) => { (r[x.area] || (r[x.area] = [])).push(x); });
    return r;
  }, [relatorio]);

  const total = (relatorio?.registos || []).length;
  const turnoLabel = TURNOS.find((t) => t.key === turno)?.label || turno;
  const confirmacoes = relatorio?.confirmacoes || [];

  const params = { secao, data, turno };

  const adicionar = async (area) => {
    const texto = (novoTexto[area] || '').trim();
    if (!texto) return;
    try {
      await axios.post(
        `${API}/api/relatorio-turno/registo`,
        { area, texto },
        { params, headers: authHeaders() }
      );
      setNovoTexto((n) => ({ ...n, [area]: '' }));
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao adicionar'); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('Eliminar este registo?')) return;
    try {
      await axios.delete(`${API}/api/relatorio-turno/registo/${id}`, {
        params, headers: authHeaders(),
      });
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao eliminar'); }
  };

  const guardarEdicao = async () => {
    if (!editando) return;
    try {
      await axios.put(
        `${API}/api/relatorio-turno/registo/${editando.id}`,
        { texto: editando.texto },
        { params, headers: authHeaders() }
      );
      setEditando(null);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha'); }
  };

  const mudarEstado = async (id, novo) => {
    try {
      await axios.patch(
        `${API}/api/relatorio-turno/registo/${id}/estado`,
        { estado: novo },
        { params, headers: authHeaders() }
      );
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha'); }
  };

  const marcarLida = async (indId) => {
    try {
      await axios.post(`${API}/api/relatorio-turno/indicacoes/${indId}/li`, {}, { headers: authHeaders() });
      onChange();
    } catch { toast.error('Falha'); }
  };

  const executarIndicacao = async (indId, estado) => {
    let justificacao = null;
    if (estado === 'nao_realizavel') {
      justificacao = window.prompt('Justificação (obrigatória):');
      if (!justificacao || !justificacao.trim()) return;
    }
    try {
      await axios.patch(
        `${API}/api/relatorio-turno/indicacoes/${indId}/execucao`,
        { estado, justificacao },
        { headers: authHeaders() }
      );
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha'); }
  };

  return (
    <Card className="p-3 border-slate-200" data-testid={`rt-bloco-${secao}-${turno}`}>
      {/* Cabeçalho colapsável */}
      <button
        className="w-full flex items-center gap-2 text-left"
        onClick={() => setAberto((v) => !v)}
        data-testid={`rt-toggle-${secao}-${turno}`}
      >
        <div className="flex-1">
          <h3 className="text-sm font-bold text-slate-900">{secao}</h3>
          <p className="text-[11px] text-slate-500">
            {turnoLabel}
            {total > 0 && <span> · {total} {total === 1 ? 'registo' : 'registos'}</span>}
            {indicacoes.length > 0 && <span className="text-purple-700"> · {indicacoes.length} indicação(ões)</span>}
          </p>
        </div>
        {confirmacoes.length > 0 && (
          <Badge className="bg-emerald-100 text-emerald-800 text-[9px]">
            ✓ {confirmacoes.length}
          </Badge>
        )}
        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${aberto ? 'rotate-90' : ''}`} />
      </button>

      {aberto && (
        <div className="mt-3 space-y-2">
          {/* Indicações */}
          {indicacoes.length > 0 && (
            <div className="p-2 rounded-lg bg-purple-50 border border-purple-200" data-testid={`rt-inds-${secao}-${turno}`}>
              <div className="flex items-center gap-1 text-xs font-semibold text-purple-900 mb-1">
                <InfoIcon className="w-3 h-3" /> Indicações
              </div>
              <div className="space-y-1.5">
                {indicacoes.map((ind) => {
                  const jaLi = (ind.leituras || []).some((l) => ehMeu(l.colaborador_id));
                  const exec = ind.execucao || {};
                  return (
                    <div key={ind.id} className={`p-2 rounded border text-xs ${
                      ind.prioridade === 'urgente' ? 'border-red-400 bg-red-50' : 'border-purple-200 bg-white'
                    }`} data-testid="rt-indicacao-item">
                      <div className="flex items-start gap-1 flex-wrap">
                        <Badge className={ind.tipo === 'a_realizar' ? 'bg-amber-500 text-white text-[9px]' : 'bg-blue-500 text-white text-[9px]'}>
                          {ind.tipo === 'a_realizar' ? 'A realizar' : 'Info'}
                        </Badge>
                        {ind.prioridade === 'urgente' && (
                          <Badge className="bg-red-500 text-white text-[9px]">URGENTE</Badge>
                        )}
                        <span className="ml-auto text-[10px] text-slate-500">{ind.criado_por_nome}</span>
                      </div>
                      <p className="mt-1 whitespace-pre-wrap">{ind.texto}</p>
                      <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                        {ind.tipo === 'info' && !jaLi && (
                          <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                                  onClick={() => marcarLida(ind.id)}
                                  data-testid={`rt-ind-li-${ind.id}`}>
                            Li
                          </Button>
                        )}
                        {ind.tipo === 'info' && jaLi && (
                          <span className="text-[10px] text-emerald-700">✓ Lida</span>
                        )}
                        {ind.tipo === 'a_realizar' && exec.estado === 'por_realizar' && (
                          <>
                            <Button size="sm" className="h-6 text-[10px] px-2 bg-emerald-600 hover:bg-emerald-700"
                                    onClick={() => executarIndicacao(ind.id, 'realizada')}
                                    data-testid={`rt-ind-realizada-${ind.id}`}>
                              Realizada
                            </Button>
                            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                                    onClick={() => executarIndicacao(ind.id, 'nao_realizavel')}
                                    data-testid={`rt-ind-nao-${ind.id}`}>
                              Não foi possível
                            </Button>
                          </>
                        )}
                        {ind.tipo === 'a_realizar' && exec.estado === 'realizada' && (
                          <span className="text-[10px] text-emerald-700">
                            ✓ Realizada por {exec.executado_por_nome} · {fmtHoraCurta(exec.executado_em)}
                          </span>
                        )}
                        {ind.tipo === 'a_realizar' && exec.estado === 'nao_realizavel' && (
                          <span className="text-[10px] text-red-700">
                            ✗ {exec.justificacao}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 4 áreas */}
          {AREAS.map((area) => {
            const AreaIcon = area.icon;
            const regs = registosPorArea[area.key] || [];
            const c = CORES_AREA[area.cor];
            return (
              <div key={area.key} className={`rounded-lg border ${c.border} ${c.bg} p-2`} data-testid={`rt-area-${secao}-${turno}-${area.key}`}>
                <div className="flex items-center gap-1 mb-1">
                  <AreaIcon className={`w-3.5 h-3.5 ${c.icon}`} />
                  <h4 className={`text-xs font-bold ${c.text}`}>{area.label}</h4>
                  <Badge variant="outline" className="ml-auto text-[9px] bg-white">{regs.length}</Badge>
                </div>

                {regs.length === 0 && (
                  <p className="text-[11px] text-slate-400 italic">Sem registos.</p>
                )}

                <div className="space-y-1">
                  {regs.map((r) => {
                    const meu = ehMeu(r.colaborador_id);
                    const editThis = editando?.id === r.id;
                    const resolvido = r.estado === 'resolvido';
                    return (
                      <div key={r.id} className={`p-2 rounded bg-white border text-xs ${resolvido ? 'opacity-70' : ''}`} data-testid="rt-registo-item">
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            {editThis ? (
                              <>
                                <Textarea
                                  value={editando.texto}
                                  onChange={(e) => setEditando({ ...editando, texto: e.target.value })}
                                  className="text-xs min-h-[50px]"
                                  data-testid={`rt-edit-textarea-${r.id}`}
                                />
                                <div className="flex gap-1 mt-1">
                                  <Button size="sm" className="h-6 text-[10px]" onClick={guardarEdicao} data-testid={`rt-edit-save-${r.id}`}>Guardar</Button>
                                  <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => setEditando(null)}>Cancelar</Button>
                                </div>
                              </>
                            ) : (
                              <p className={`whitespace-pre-wrap break-words ${resolvido ? 'line-through text-slate-500' : ''}`}>{r.texto}</p>
                            )}
                            <div className="text-[10px] text-slate-500 mt-1 flex flex-wrap gap-x-1.5 gap-y-0.5">
                              <span>por {r.colaborador_nome}</span>
                              <span>·</span>
                              <span>{fmtHora(r.criado_em)}</span>
                              {r.atualizado_em && r.atualizado_em !== r.criado_em && <span className="text-slate-400">(editado)</span>}
                              {resolvido && r.resolvido_por_nome && (
                                <span className="text-emerald-700 w-full">
                                  ✓ Concluída por {r.resolvido_por_nome} às {fmtHoraCurta(r.resolvido_em)}
                                </span>
                              )}
                            </div>
                          </div>

                          {!editThis && (
                            <div className="flex flex-col gap-1 shrink-0">
                              {/* Dar como concluída (qualquer user, tarefas geríveis) */}
                              {r.estado === 'pendente' && (
                                <Button
                                  size="sm" variant="outline" className="h-7 text-[10px] px-2 border-emerald-300 hover:bg-emerald-50"
                                  title="Dar como concluída"
                                  onClick={() => mudarEstado(r.id, 'resolvido')}
                                  data-testid={`rt-concluir-${r.id}`}
                                >
                                  <CheckCircle2 className="w-3 h-3 text-emerald-600 mr-0.5" />
                                  <span className="hidden sm:inline">Concluir</span>
                                </Button>
                              )}
                              {/* Reabrir (só admin) */}
                              {resolvido && isAdmin && (
                                <Button
                                  size="sm" variant="outline" className="h-7 text-[10px] px-2"
                                  title="Reabrir"
                                  onClick={() => mudarEstado(r.id, 'pendente')}
                                  data-testid={`rt-reabrir-${r.id}`}
                                >
                                  <RefreshCw className="w-3 h-3 mr-0.5" />
                                  <span className="hidden sm:inline">Reabrir</span>
                                </Button>
                              )}
                              {/* Editar (autor ou admin) */}
                              {(meu || isAdmin) && !resolvido && (
                                <Button
                                  size="sm" variant="outline" className="h-7 w-7 p-0"
                                  title="Editar"
                                  onClick={() => setEditando({ id: r.id, texto: r.texto })}
                                  data-testid={`rt-editar-${r.id}`}
                                >
                                  <Edit3 className="w-3 h-3" />
                                </Button>
                              )}
                              {/* Apagar (autor ou admin) */}
                              {(meu || isAdmin) && (
                                <Button
                                  size="sm" variant="outline" className="h-7 w-7 p-0 hover:bg-red-50"
                                  title="Apagar"
                                  onClick={() => eliminar(r.id)}
                                  data-testid={`rt-eliminar-${r.id}`}
                                >
                                  <Trash2 className="w-3 h-3 text-red-600" />
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Adicionar (só se pode adicionar aqui) */}
                {podeAdicionar && (
                  <div className="mt-1.5 flex gap-1">
                    <Input
                      placeholder="Adicionar…"
                      value={novoTexto[area.key]}
                      onChange={(e) => setNovoTexto((n) => ({ ...n, [area.key]: e.target.value }))}
                      onKeyDown={(e) => e.key === 'Enter' && adicionar(area.key)}
                      className="text-xs h-8 bg-white"
                      data-testid={`rt-adicionar-input-${secao}-${turno}-${area.key}`}
                    />
                    <Button size="sm" className="h-8 px-2"
                            onClick={() => adicionar(area.key)}
                            data-testid={`rt-adicionar-btn-${secao}-${turno}-${area.key}`}>
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}

          {/* Confirmações neste (secção,turno) */}
          {confirmacoes.length > 0 && (
            <div className="text-[10px] text-emerald-700 border-t pt-1.5" data-testid={`rt-confirmacoes-${secao}-${turno}`}>
              ✓ Confirmado por: {confirmacoes.map((c) => c.colaborador_nome).join(', ')}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

// ==================== ADMIN — Criar indicação ====================
const BlocoCriarIndicacao = ({ data, seccoes, onCriado }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    secao: '',
    data,
    turno_ou_periodo: 'dia',
    texto: '',
    tipo: 'info',
    prioridade: 'normal',
  });

  useEffect(() => { setForm((f) => ({ ...f, data })); }, [data]);

  const criar = async () => {
    if (!form.secao) { toast.error('Escolha uma secção'); return; }
    if (!form.data)  { toast.error('Escolha uma data'); return; }
    if (!form.texto.trim()) { toast.error('Escreva o texto da indicação'); return; }
    try {
      await axios.post(`${API}/api/relatorio-turno/indicacoes`, form, { headers: authHeaders() });
      toast.success('Indicação criada');
      setForm((f) => ({ ...f, texto: '' }));
      setOpen(false);
      onCriado?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha'); }
  };

  return (
    <Card className="p-3 border-dashed border-slate-300" data-testid="rt-admin-bloco">
      <button
        className="w-full flex items-center gap-2 text-sm font-semibold text-slate-700"
        onClick={() => setOpen(!open)}
        data-testid="rt-admin-toggle"
      >
        <MessageSquareText className="w-4 h-4" />
        <span>Criar indicação</span>
        <ChevronRight className={`w-4 h-4 ml-auto transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <select
              className="text-sm border rounded px-2 py-1"
              value={form.secao}
              onChange={(e) => setForm({ ...form, secao: e.target.value })}
              data-testid="rt-admin-secao"
            >
              <option value="">Secção…</option>
              {seccoes.map((s) => <option key={s.key} value={s.label}>{s.label}</option>)}
            </select>
            <input
              type="date"
              className="text-sm border rounded px-2 py-1"
              value={form.data}
              onChange={(e) => setForm({ ...form, data: e.target.value })}
              data-testid="rt-admin-data"
            />
            <select
              className="text-sm border rounded px-2 py-1"
              value={form.turno_ou_periodo}
              onChange={(e) => setForm({ ...form, turno_ou_periodo: e.target.value })}
              data-testid="rt-admin-turno"
            >
              <option value="dia">Dia inteiro</option>
              <option value="manha">Manhã</option>
              <option value="tarde">Tarde</option>
            </select>
            <select
              className="text-sm border rounded px-2 py-1"
              value={form.tipo}
              onChange={(e) => setForm({ ...form, tipo: e.target.value })}
              data-testid="rt-admin-tipo"
            >
              <option value="info">Informação</option>
              <option value="a_realizar">A realizar</option>
            </select>
            <select
              className="text-sm border rounded px-2 py-1 col-span-2"
              value={form.prioridade}
              onChange={(e) => setForm({ ...form, prioridade: e.target.value })}
              data-testid="rt-admin-prioridade"
            >
              <option value="normal">Prioridade Normal</option>
              <option value="urgente">Urgente</option>
            </select>
          </div>
          <Textarea
            placeholder="Texto da indicação…"
            value={form.texto}
            onChange={(e) => setForm({ ...form, texto: e.target.value })}
            className="text-sm min-h-[60px]"
            data-testid="rt-admin-texto"
          />
          <Button className="w-full h-9" onClick={criar} data-testid="rt-admin-criar">
            <Plus className="w-4 h-4 mr-1" /> Criar indicação
          </Button>
        </div>
      )}
    </Card>
  );
};

// ==================== HISTÓRICO ====================
const HistoricoModal = ({ seccoes, onClose }) => {
  const [filtros, setFiltros] = useState({
    secao: '', data_inicio: '', data_fim: '',
    estado_passagem: '', situacao: '',
  });
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(false);

  const buscar = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filtros).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await axios.get(`${API}/api/relatorio-turno/historico`, { params, headers: authHeaders() });
      setResultados(r.data || []);
    } catch { toast.error('Falha'); }
    finally { setLoading(false); }
  };

  useEffect(() => { buscar(); }, []);

  const limparFiltros = () => setFiltros({ secao: '', data_inicio: '', data_fim: '', estado_passagem: '', situacao: '' });

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-4" onClick={(e) => e.stopPropagation()} data-testid="rt-historico-modal">
        <div className="sticky top-0 bg-white pb-2 border-b flex items-center gap-2 mb-3">
          <Clock className="w-5 h-5" />
          <h2 className="font-bold">Histórico de Relatórios</h2>
          <button className="ml-auto text-slate-500 text-xl" onClick={onClose} data-testid="rt-historico-fechar">✕</button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
          <select className="text-sm border rounded px-2 py-1"
                  value={filtros.secao} onChange={(e) => setFiltros({ ...filtros, secao: e.target.value })}
                  data-testid="rt-hist-secao">
            <option value="">Todas as secções</option>
            {seccoes.map((s) => <option key={s.key} value={s.label}>{s.label}</option>)}
          </select>
          <input type="date" className="text-sm border rounded px-2 py-1"
                 value={filtros.data_inicio}
                 onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })}
                 data-testid="rt-hist-data-ini" />
          <input type="date" className="text-sm border rounded px-2 py-1"
                 value={filtros.data_fim}
                 onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })}
                 data-testid="rt-hist-data-fim" />
          <select className="text-sm border rounded px-2 py-1"
                  value={filtros.estado_passagem} onChange={(e) => setFiltros({ ...filtros, estado_passagem: e.target.value })}
                  data-testid="rt-hist-estado">
            <option value="">Estado (todos)</option>
            <option value="por_confirmar">Por confirmar</option>
            <option value="confirmado">Confirmado</option>
          </select>
          <select className="text-sm border rounded px-2 py-1 col-span-2 sm:col-span-1"
                  value={filtros.situacao} onChange={(e) => setFiltros({ ...filtros, situacao: e.target.value })}
                  data-testid="rt-hist-situacao">
            <option value="">Situação (todos)</option>
            <option value="com_pendentes">Com pendentes</option>
            <option value="sem_pendentes">Sem pendentes</option>
          </select>
        </div>
        <div className="flex gap-2 mb-3">
          <Button size="sm" onClick={buscar} data-testid="rt-hist-filtrar">Filtrar</Button>
          <Button size="sm" variant="outline" onClick={() => { limparFiltros(); buscar(); }} data-testid="rt-hist-limpar">Limpar filtros</Button>
        </div>
        {loading && <p className="text-xs text-slate-500">A carregar…</p>}
        <div className="space-y-1.5">
          {resultados.map((r) => (
            <div key={r.id} className="p-2 border rounded text-xs" data-testid="rt-hist-item">
              <div className="flex items-center justify-between">
                <span className="font-semibold">{r.data} · {TURNOS.find((t) => t.key === r.turno)?.label || r.turno} · {r.secao}</span>
                <div className="flex gap-1">
                  {r._num_pendentes > 0 && (
                    <Badge className="bg-amber-100 text-amber-800 text-[9px]">{r._num_pendentes} pendentes</Badge>
                  )}
                  <Badge className={r.estado === 'confirmado' ? 'bg-emerald-100 text-emerald-800 text-[9px]' : 'bg-slate-100 text-slate-700 text-[9px]'}>
                    {r.estado === 'confirmado' ? 'Confirmado' : 'Aberto'}
                  </Badge>
                </div>
              </div>
              <p className="text-[10px] text-slate-500 mt-1">
                {r._num_registos} registos · {r.confirmacoes?.length || 0} confirmações
              </p>
            </div>
          ))}
          {!loading && resultados.length === 0 && (
            <p className="text-xs text-slate-500 italic">Sem resultados.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RelatorioTurnoTab;
